import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { defaultQueueMetricsParams } from '@/utils/metricsParams';
import { MetricsSection } from './MetricsSection';
import { api } from '@/store/api';
import type { ServiceLevelTargetSeconds } from '@/types/agentEvents';
import {
  ADHERENCE_GROUPINGS,
  MetricIntervalPeriod,
  VALID_SERVICE_LEVEL_TARGETS,
} from '@/types/agentEvents';
import { Box, Typography } from '@gapartners/ui';
import { IntervalMetricsChartWithSeries } from '../common/IntervalMetricsChartWithSeries';
import { MetricCard } from '../common/MetricCard';
import { MetricIntervalLineChart } from '../common/MetricIntervalLineChart';

const { useGetServiceLevelMetricsQuery } = api;

export function ServiceLevelSection() {
  const { t } = useTranslation();
  const [targetSeconds, setTargetSeconds] =
    useState<ServiceLevelTargetSeconds>(20);

  return (
    <MetricsSection
      titleKey="components.dashboard.serviceLevel"
      descriptionKey="components.dashboard.serviceLevelDescription"
      metricName="service-level"
      defaultParams={defaultQueueMetricsParams()}
      useQuery={useGetServiceLevelMetricsQuery}
      groupingsOptions={ADHERENCE_GROUPINGS}
      refreshLabelKey="components.dashboard.refreshServiceLevel"
      extraParams={{ targetSeconds }}
      extraControls={() => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {t('components.dashboard.targetSeconds')}
          </Typography>
          <Box
            component="select"
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              px: 1.5,
              py: 1,
              minWidth: 120,
            }}
            value={targetSeconds}
            onChange={e =>
              setTargetSeconds(
                Number(
                  (e.target as HTMLSelectElement).value
                ) as ServiceLevelTargetSeconds
              )
            }
          >
            {VALID_SERVICE_LEVEL_TARGETS.map(s => (
              <Box component="option" key={s} value={s}>
                {s} s
              </Box>
            ))}
          </Box>
        </Box>
      )}
      renderData={({ data, params, metricValue, groupingKey }) => {
        const showIntervalChart =
          params.intervalPeriod !== MetricIntervalPeriod.TOTAL &&
          data.some(item => item.intervalStartTime != null);

        if (showIntervalChart) {
          return (
            <Box sx={{ width: '100%', minHeight: 320, mt: 1 }}>
              <IntervalMetricsChartWithSeries
                data={data}
                groupingKey={groupingKey}
                valueToNumber={metricValue}
                valueFormatter={v => `${metricValue(v).toFixed(1)}%`}
                height={300}
                IntervalChartComponent={MetricIntervalLineChart}
              />
            </Box>
          );
        }

        return (
          <Box
            sx={{
              mt: 2,
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)',
                xl: 'repeat(5, 1fr)',
              },
              gap: 2,
            }}
          >
            {data.map(item => (
              <MetricCard
                key={item.id}
                title={
                  item.dimensionLabels?.[groupingKey] ?? item.name ?? 'N/A'
                }
                value={metricValue(item.value)}
                max={100}
                suffix="%"
              />
            ))}
          </Box>
        );
      }}
    />
  );
}
